import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { PromoCode, AlertType, Error } from '../types/common';
import { CREATE_PROMO_ERROR_MESSAGE, DELETE_PROMO_ERROR_MESSAGE, FETCH_PROMO_ERROR_MESSAGE, UPDATE_PROMO_ERROR_MESSAGE } from '../constants/messages';
import { showAlert } from './CommonSlice';
import { getCsrfToken } from '../utils/helpers';

/* ----------------------------- State ----------------------------- */
export type PromoCodeState = {
    promoCodes: PromoCode[];
    selectedPromoCode: Partial<PromoCode>;
    isLoading: boolean;
    error: Error;
};

const initialState: PromoCodeState = {
    promoCodes: [],
    selectedPromoCode: {},
    isLoading: false,
    error: { isError: false, message: '' },
};

const BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';

/* -------------------------- helpers ------------------------------ */
const normalizePromo = (p: any): PromoCode => {
    const { _id, id, ...rest } = p;
    return { id: (_id ?? id) as string, ...rest } as PromoCode;
};

/* --------------------------- Thunks ------------------------------ */
export const fetchPromoCodes = createAsyncThunk('promocode/fetchPromoCodes', async (_, { dispatch, rejectWithValue }) => {
    const response = await fetch(`${BASE_URL}/promocodes`, {
        credentials: 'include',
    });

    if (!response.ok) {
        dispatch(showAlert({ type: AlertType.Error, message: FETCH_PROMO_ERROR_MESSAGE }));
        return rejectWithValue(FETCH_PROMO_ERROR_MESSAGE);
    }

    const data = await response.json();
    const promoCodes: PromoCode[] = data.map(normalizePromo);
    return promoCodes;
});

export const createPromoCode = createAsyncThunk(
    'promocode/createPromoCode',
    async (promo: Omit<PromoCode, 'id' | 'usageCount' | 'createdAt' | 'status'>, { dispatch, rejectWithValue }) => {
        const csrfToken = await getCsrfToken();
        const body = {
            ...promo,
            usageCount: 0,
            status: 'active',
            createdAt: new Date().toISOString(),
        };

        const response = await fetch(`${BASE_URL}/promocodes`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-type': 'application/json',
                'csrf-token': csrfToken,
            },
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            const { error } = await response.json();
            dispatch(showAlert({ type: AlertType.Error, message: error || CREATE_PROMO_ERROR_MESSAGE }));
            return rejectWithValue(error || CREATE_PROMO_ERROR_MESSAGE);
        }

        const data = await response.json();
        dispatch(showAlert({ type: AlertType.Success, message: 'Промо-код создан' }));
        return normalizePromo(data);
    },
);

export const updatePromoCodeStatus = createAsyncThunk(
    'promocode/updatePromoCodeStatus',
    async ({ id, status }: { id: string; status: PromoCode['status'] }, { dispatch, rejectWithValue }) => {
        const csrfToken = await getCsrfToken();
        const response = await fetch(`${BASE_URL}/promocodes/${id}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: {
                'Content-type': 'application/json',
                'csrf-token': csrfToken,
            },
            body: JSON.stringify({ status }),
        });

        if (!response.ok) {
            const { error } = await response.json();
            dispatch(showAlert({ type: AlertType.Error, message: error || UPDATE_PROMO_ERROR_MESSAGE }));
            return rejectWithValue(error || UPDATE_PROMO_ERROR_MESSAGE);
        }

        const data = await response.json();
        dispatch(showAlert({ type: AlertType.Success, message: 'Статус обновлён' }));
        return normalizePromo(data);
    },
);

export const deletePromoCode = createAsyncThunk('promocode/deletePromoCode', async (id: string, { dispatch, rejectWithValue }) => {
    const csrfToken = await getCsrfToken();
    const response = await fetch(`${BASE_URL}/promocodes/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
            'Content-type': 'application/json',
            'csrf-token': csrfToken,
        },
    });

    if (!response.ok) {
        const { error } = await response.json();
        dispatch(showAlert({ type: AlertType.Error, message: error || DELETE_PROMO_ERROR_MESSAGE }));
        return rejectWithValue(error || DELETE_PROMO_ERROR_MESSAGE);
    }

    dispatch(showAlert({ type: AlertType.Success, message: 'Промо-код удалён' }));
    return id;
});

/* ---------------------------- Slice ------------------------------ */
const promoCodeSlice = createSlice({
    name: 'promocode',
    initialState,
    reducers: {
        selectPromoCode(state, action: PayloadAction<string>) {
            state.selectedPromoCode = state.promoCodes.find((p) => p.id === action.payload) || {};
        },
        removeSelectedPromoCode(state) {
            state.selectedPromoCode = {};
        },
    },
    extraReducers: (builder) => {
        builder
            /* fetch */
            .addCase(fetchPromoCodes.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchPromoCodes.fulfilled, (state, { payload }) => {
                state.promoCodes = payload;
                state.isLoading = false;
            })
            .addCase(fetchPromoCodes.rejected, (state, { payload }) => {
                state.error = { isError: true, message: payload as string };
                state.isLoading = false;
            })

            /* create */
            .addCase(createPromoCode.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(createPromoCode.fulfilled, (state, { payload }) => {
                state.promoCodes.push(payload);
                state.isLoading = false;
            })
            .addCase(createPromoCode.rejected, (state, { payload }) => {
                state.error = { isError: true, message: payload as string };
                state.isLoading = false;
            })

            /* update status */
            .addCase(updatePromoCodeStatus.fulfilled, (state, { payload }) => {
                state.promoCodes = state.promoCodes.map((p) => (p.id === payload.id ? payload : p));
            })

            /* delete */
            .addCase(deletePromoCode.fulfilled, (state, { payload }) => {
                state.promoCodes = state.promoCodes.filter((p) => p.id !== payload);
            })

            /* any rejected */
            .addMatcher(
                (action) => action.type.startsWith('promocode/') && action.type.endsWith('/rejected'),
                (state, action) => {
                    state.error = { isError: true, message: action.payload as string };
                },
            );
    },
});

export const { selectPromoCode, removeSelectedPromoCode } = promoCodeSlice.actions;
export default promoCodeSlice.reducer;
